import { db, storage } from "./firebase";
import { collection, addDoc, onSnapshot, query, where, serverTimestamp, orderBy, writeBatch, doc, getDocs } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';

export const uploadMessageAttachment = (projectId, file, progressCallback) => {
    return new Promise((resolve, reject) => {
        const filePath = `projects/${projectId}/message_attachments/${uuidv4()}-${file.name}`;
        const storageRef = ref(storage, filePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                if (progressCallback) progressCallback(progress);
            },
            (error) => {
                console.error("Attachment upload failed:", error);
                reject(error);
            },
            async () => {
                try {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                } catch (error) {
                    console.error("Failed to get download URL for attachment:", error);
                    reject(error);
                }
            }
        );
    });
};

export const onMessagesChange = (projectId, elementId, callback) => {
    const messagesRef = collection(db, 'projects', projectId, 'messages');
    // The previous query required a composite index. By removing the orderBy clause,
    // we avoid the need for the index and perform sorting on the client-side.
    const q = query(messagesRef, where("elementId", "==", elementId));
    
    return onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Sort messages by creation date on the client
        const sortedMessages = messages.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateA - dateB;
        });

        callback(sortedMessages);
    }, (error) => {
        console.error("Error listening to messages:", error);
    });
};

export const addMessage = async (projectId, elementId, messageData, participants) => {
    const messagesRef = collection(db, 'projects', projectId, 'messages');
    
    const messageDocRef = await addDoc(messagesRef, {
        ...messageData,
        projectId,
        elementId,
        createdAt: serverTimestamp()
    });

    // Create notifications for all participants except the sender
    const batch = writeBatch(db);
    const senderId = messageData.userId;

    participants.forEach(userId => {
        if (userId !== senderId) {
            const notificationRef = doc(collection(db, 'users', userId, 'notifications'));
            batch.set(notificationRef, {
                userId,
                messageId: messageDocRef.id,
                projectId,
                elementId,
                read: false,
                createdAt: serverTimestamp()
            });
        }
    });

    await batch.commit();
};

export const clearAllProjectMessages = async (projectId) => {
    const messagesRef = collection(db, 'projects', projectId, 'messages');
    const messagesSnapshot = await getDocs(messagesRef);

    if (messagesSnapshot.empty) {
        return;
    }

    // Delete associated attachments from Storage
    const deletePromises = [];
    messagesSnapshot.docs.forEach(doc => {
        const messageData = doc.data();
        if (messageData.imageUrl && messageData.imageUrl.includes('firebasestorage.googleapis.com')) {
            const fileRef = ref(storage, messageData.imageUrl);
            deletePromises.push(deleteObject(fileRef).catch(err => {
                // It's okay if the object is not found, log other errors.
                if (err.code !== 'storage/object-not-found') {
                    console.warn(`Failed to delete message attachment ${messageData.imageUrl}:`, err);
                }
            }));
        }
    });
    await Promise.all(deletePromises);

    // Delete all message documents in a batch
    const batch = writeBatch(db);
    messagesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
    });

    await batch.commit();
};
